import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Briefcase, Clock } from "lucide-react";
import { PageHero, SectionHeading } from "../components/shared";
import { Reveal } from "../components/Reveal";
import { JOB_CATEGORIES, IMAGES } from "../data/content";
import { api, formatError } from "../lib/api";

const Field = ({ label, name, form, set, type = "text", required, options }) => (
  <label className="block">
    <span className="text-sm font-medium text-[#252B33]">{label}{required && <span className="text-[#F28C28]"> *</span>}</span>
    {options ? (
      <select value={form[name]} onChange={(e) => set(name, e.target.value)} required={required} data-testid={`career-${name}`}
        className="mt-1.5 w-full border border-[#E2E8F0] rounded-md px-3 py-2.5 bg-white focus:border-[#1EA7E1] outline-none">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input type={type} value={form[name]} onChange={(e) => set(name, e.target.value)} required={required} data-testid={`career-${name}`}
        className="mt-1.5 w-full border border-[#E2E8F0] rounded-md px-3 py-2.5 focus:border-[#1EA7E1] outline-none" />
    )}
  </label>
);

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const empty = { full_name: "", email: "", telephone: "", current_location: "", preferred_location: "", role: "", experience: "", current_employer: "", notice_period: "", expected_salary: "", message: "" };
  const [form, setForm] = useState(empty);
  const [cv, setCv] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/jobs").then(({ data }) => setJobs(Array.isArray(data) ? data : [])).catch(() => setJobs([])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!cv) return toast.error("Please attach your CV (PDF or Word).");
    if (!consent) return toast.error("Please provide consent to proceed.");
    if (cv.size > 5 * 1024 * 1024) return toast.error("CV exceeds the 5MB limit.");
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("consent", "true");
      fd.append("cv", cv);
      const { data } = await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message);
      setForm(empty); setCv(null); setConsent(false);
      e.target.reset();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Submission failed.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <PageHero overline="Careers" title="Build India's Connected Future with Airanet" subtitle="We connect with telecom engineers, field technicians, project managers, civil and electrical professionals, fibre technicians and site supervisors." image={IMAGES.team} />

      <section className="py-24 bg-white">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <SectionHeading overline="Current Openings" title="Roles We Hire For" sub="Live vacancies are published by our team. General applications are always welcome." />
          {Array.isArray(jobs) && jobs.length > 0 && (
            <div className="mt-12 grid md:grid-cols-2 gap-5">
              {jobs.map((j) => (
                <Reveal key={j.id}>
                  <div className="bg-[#F4F6F8] border border-[#E2E8F0] rounded-lg p-6 hover:border-[#1EA7E1] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-head font-semibold text-lg text-[#0B1F3A]">{j.title}</h3>
                      <span className="text-xs bg-[#0B1F3A] text-white px-2.5 py-1 rounded">{j.category}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#64748B]">
                      <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{j.location}</span>
                      <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4" />{j.type}</span>
                      {j.experience && <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />{j.experience}</span>}
                    </div>
                    {j.description && <p className="mt-3 text-sm text-[#64748B]">{j.description}</p>}
                    <a href="#apply" className="mt-4 inline-block text-sm font-semibold text-[#1261A0]">Apply for this role →</a>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {JOB_CATEGORIES.map((c) => <span key={c} className="bg-white border border-[#E2E8F0] rounded-md px-4 py-3 text-sm text-[#252B33]">{c}</span>)}
          </div>
        </div>
      </section>

      <section id="apply" className="py-24 bg-[#F4F6F8] scroll-mt-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <SectionHeading overline="Submit Your CV" title="Apply to Join Airanet" center />
          <form onSubmit={submit} className="mt-10 bg-white border border-[#E2E8F0] rounded-lg p-7 grid sm:grid-cols-2 gap-5" data-testid="career-form">
            <Field label="Full name" name="full_name" form={form} set={set} required />
            <Field label="Email" name="email" type="email" form={form} set={set} required />
            <Field label="Telephone" name="telephone" form={form} set={set} required />
            <Field label="Current location" name="current_location" form={form} set={set} />
            <Field label="Preferred work location" name="preferred_location" form={form} set={set} />
            <Field label="Role applied for" name="role" form={form} set={set} required options={JOB_CATEGORIES} />
            <Field label="Years of experience" name="experience" form={form} set={set} />
            <Field label="Current employer" name="current_employer" form={form} set={set} />
            <Field label="Notice period" name="notice_period" form={form} set={set} />
            <Field label="Expected salary" name="expected_salary" form={form} set={set} />
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-[#252B33]">Message</span>
              <textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="career-message" className="mt-1.5 w-full border border-[#E2E8F0] rounded-md px-3 py-2.5 focus:border-[#1EA7E1] outline-none" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-[#252B33]">Upload CV (PDF/DOC, max 5MB) <span className="text-[#F28C28]">*</span></span>
              <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCv(e.target.files[0])} data-testid="career-cv" className="mt-1.5 w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#0B1F3A] file:text-white" />
            </label>
            <label className="flex items-start gap-2 sm:col-span-2 text-sm text-[#64748B]">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="career-consent" className="mt-1" />
              I consent to Airanet storing and processing my details for recruitment purposes.
            </label>
            <button disabled={loading} data-testid="career-submit" className="sm:col-span-2 bg-[#F28C28] text-[#0B1F3A] font-semibold py-3.5 rounded-md hover:bg-[#e07d1c] disabled:opacity-60">{loading ? "Submitting…" : "Submit Application"}</button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Careers;
